'use client'

import { BranchSelect } from '@repo/ui/BranchSelect'
import { ClientSelect } from '@repo/ui/ClientSelect'
import { DateSelection } from '@repo/ui/DateSelection'
import { DepartmentSelect } from '@repo/ui/DepartmentSelect'
import { LargeAddMatterIcon } from '@repo/ui/LargeAddMatterIcon'
import { matterDetailsSchema } from '@repo/validation/schema_NewMatter'
import { NewMatterNumberEntry } from '@repo/ui/NewMatterNumberEntry'
import { TextEntry } from '@repo/ui/TextEntry'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useEffect, useState, useRef } from 'react'
import { useToastStore } from '@repo/store/useToastStore'
import { validate } from '@repo/validation/validation'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardPrompt } from '@repo/ui/WizardPrompt'
import { WorktypeSelect } from '@repo/ui/WorktypeSelect'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import PropTypes from 'prop-types'

export const NewMatterDetails = ({ updateStepState, wideMode, handlePrevNext, testData, wizardId, currentStep }) => {
  const {
    newClientNo: clientNoFromClientWizard,
    isNew: clientIsNew,
    existingClient,
    updateState: updateClientState,
    resetWizardClientState
  } = useNewClientStore()
  const {
    prospect,
    dateOpened,
    description,
    worktype,
    branch,
    department,
    updateState,
    newMatterNo,
    resetWizardMatterState
  } = useNewMatterStore()
  const [stateToSave, setStateToSave] = useState({})
  const [validationErrors, setValidationErrors] = useState({})
  const [matterNoError, setMatterNoError] = useState(null)
  const { showToast } = useToastStore()
  const formRef = useRef()

  useEffect(() => {
    setStateToSave({
      prospect,
      dateOpened,
      description,
      worktype,
      branch,
      department
    })
  }, [prospect, dateOpened, description, worktype, branch, department])

  const handleClickBack = async () => {
    await resetWizardClientState()
    await resetWizardMatterState()

    window.location.href.includes('?wizard=NewMatter-NotificationApproval&stage=matter&step=matter-details')
      ? handlePrevNext(-2)
      : handlePrevNext(-1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (matterNoError) return

    setValidationErrors({})

    try {
      await validate(matterDetailsSchema, stateToSave)
    } catch (err) {
      const { inner } = err
      const errors = {}
      let firstErrorKey
      inner.forEach(({ path, message }, index) => {
        errors[path] = message
        if (index === 0) firstErrorKey = CSS.escape(path)
      })

      console.log(errors)

      showToast({ type: 'error', text: 'Please check all fields for errors', title: 'Form errors' })

      // scroll to error if possible
      const container = document.querySelector('[data-testid="WizardStepContentsContainer"]')
      const errorElement = firstErrorKey
        ? document.querySelector(`[data-testid="WizardPanel"]:has([name=${firstErrorKey}])`)
        : null
      if (container && errorElement) container.scrollTop = errorElement.offsetParent?.offsetTop || 0

      setValidationErrors(errors)
      return
    }

    updateStepState({ status: 'completed', statusText: description })
    handlePrevNext(1)
  }

  const updateDate = (value) => {
    updateState({ key: 'dateOpened', value })
    updateState({ key: 'uniqueFileNoDate', value }) // this is used later in the wizard
    updateState({ key: 'uniqueFileNo', value: null }) // reset unique file no
  }

  const selectExistingClient = (value) => {
    updateClientState({ key: 'isNew', value: false })
    updateClientState({ key: 'existingClient', value })
  }

  if (!dateOpened) updateDate(new Date())

  const date = dateOpened && typeof dateOpened === 'string' ? new Date(dateOpened) : dateOpened || new Date()
  const clientIsSelected = clientIsNew ? clientNoFromClientWizard : existingClient
  const matterNoIsSet = newMatterNo || newMatterNo === 0

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      {currentStep?.index === 0 ? <WizardPrompt wizardId={wizardId} /> : null}

      <WizardPanel data-testid="NewMatterDetails">
        <WizardFormSection>
          <WizardIconTitle title={'Matter details'} icon={LargeAddMatterIcon} sx={{ mb: 8, width: '100%' }} />

          {clientIsNew && wizardId !== 'ExistingClient-NewMatter' ? (
            <TextEntry
              size="medium"
              value={clientNoFromClientWizard}
              label="Client no"
              sx={{ flex: 1 }}
              limit={10}
              readOnly
            />
          ) : (
            <ClientSelect
              size="medium"
              sx={{ flex: 1, minWidth: '320px' }}
              value={existingClient}
              onChange={selectExistingClient}
              required
              name="client"
              errors={validationErrors}
            />
          )}

          <NewMatterNumberEntry
            size="medium"
            onChange={(value) => updateState({ key: 'newMatterNo', value })}
            clientNo={clientIsNew ? clientNoFromClientWizard : existingClient?.clientNo || null}
            sx={{ width: '200px' }}
            onError={setMatterNoError}
            testMode={testData}
          />

          <DateSelection
            format="d MMM yyyy"
            value={date}
            onChange={updateDate}
            size="medium"
            label="Date opened"
            sx={{ flex: 1, minWidth: '200px' }}
            required
            enableTypedDdMmYyyy={true}
            showAdornment={true}
            largeAdornment={true}
          />

          <TextEntry
            size="medium"
            value={description}
            onChange={(value) => updateState({ key: 'description', value })}
            label="Matter description"
            sx={{ width: '100%' }}
            limit={255}
            required
          />

          <MUIFormControlLabel
            sx={{ width: 'fit-content', ml: -2, mb: 4, mt: 2, mr: 100 }}
            control={
              <MUICheckbox
                checked={prospect}
                onChange={(e) => updateState({ key: 'prospect', value: e.target.checked })}
              />
            }
            label="This matter is a prospect"
          />

          <WorktypeSelect
            value={worktype}
            onChange={(value) => updateState({ key: 'worktype', value })}
            size="medium"
            sx={{ width: '100%', maxWidth: '486px' }}
            errors={validationErrors}
            name={`worktype`}
            required
            testData={testData}
          />

          <BranchSelect
            value={branch}
            onChange={(value) => {
              updateState({ key: 'branch', value })
            }}
            size="medium"
            sx={{ width: '100%', maxWidth: '486px' }}
            errors={validationErrors}
            name={`branch`}
            required
            testData={testData}
          />

          <DepartmentSelect
            value={department}
            onChange={(value) => updateState({ key: 'department', value })}
            size="medium"
            sx={{ width: '100%', maxWidth: '486px' }}
            errors={validationErrors}
            name={`department`}
            required
            testData={testData}
          />
        </WizardFormSection>
      </WizardPanel>

      <WizardFooter
        wideMode={wideMode}
        handleClickBack={handleClickBack}
        continueButtonIsFormSubmitButton
        backButtonVisible={currentStep?.index !== 0}
        continueButtonDisabled={!clientIsSelected || !matterNoIsSet}
      />
    </form>
  )
}

NewMatterDetails.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testData: PropTypes.array,
  wizardId: PropTypes.string,
  uniqueStepId: PropTypes.string
}
